import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { ArrowLeft, ChevronLeft, ChevronRight, Archive, ArchiveRestore, RefreshCw, Loader2, CheckCircle2, XCircle, Undo2, Building } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function TicketDetailNav({ ticket, onBack, onPrev, onNext, hasPrev, hasNext, onToggleArchive, isUpdating, onRefresh, isRefreshing, onQuickAction, quickActionUndoMode, onManageCompany }) {
    const { t } = useTranslation();
    const isArchived = !!ticket?.archivedAt;
    const inCloseUndo = quickActionUndoMode === "close";
    const inResolveUndo = quickActionUndoMode === "resolve";
    const showCompany = ticket?.company?.id && onManageCompany;
    const archiveIcon = isUpdating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : isArchived ? <ArchiveRestore className="w-4 h-4 mr-1.5" /> : <Archive className="w-4 h-4 mr-1.5" />;
    const archiveLabel = isArchived ? t("admin.support.unarchive", "Unarchive") : t("admin.support.archive", "Archive");
    const resolveIcon = inResolveUndo ? <Undo2 className="w-4 h-4 mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />;
    const resolveLabel = inResolveUndo ? t("admin.support.undoResolve", "Undo resolve") : t("admin.support.resolveAndArchive", "Resolve & Archive");
    const closeIcon = inCloseUndo ? <Undo2 className="w-4 h-4 mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />;
    const closeLabel = inCloseUndo ? t("admin.support.undoClose", "Undo close") : t("admin.support.closeAndArchive", "Close & Archive");

    return (
        <div>
            {/* MOBILE */}
            <div className="sm:hidden space-y-2">
                <div className="flex items-center justify-between gap-1">
                    <Button variant="ghost" onClick={onBack} size="sm" className="-ml-2">
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        <span className="text-sm">{t("admin.support.backToTickets", "Back")}</span>
                    </Button>
                    <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-9 w-9" disabled={!hasPrev} onClick={onPrev}><ChevronLeft className="w-4 h-4" /></Button>
                        <Button variant="outline" size="icon" className="h-9 w-9" disabled={!hasNext} onClick={onNext}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    <Button variant={inResolveUndo ? "outline" : "secondary"} className="h-10 justify-center" onClick={() => onQuickAction?.("resolve")} disabled={isUpdating || !ticket}>
                        {resolveIcon}<span className="text-sm">{resolveLabel}</span>
                    </Button>
                    <Button variant={inCloseUndo ? "outline" : "secondary"} className="h-10 justify-center" onClick={() => onQuickAction?.("close")} disabled={isUpdating || !ticket}>
                        {closeIcon}<span className="text-sm">{closeLabel}</span>
                    </Button>
                </div>
                <div className={`grid ${showCompany ? "grid-cols-3" : "grid-cols-2"} gap-1.5`}>
                    <Button variant="outline" className="h-10 justify-center" onClick={onRefresh} disabled={isRefreshing || !ticket}>
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${isRefreshing ? "animate-spin" : ""}`} />
                        <span className="text-sm">{t("common.refresh", "Refresh")}</span>
                    </Button>
                    {showCompany && (
                        <Button variant="outline" className="h-10 justify-center" onClick={onManageCompany}>
                            <Building className="w-4 h-4 mr-1.5" />
                            <span className="text-sm truncate">{t("admin.manageCompany.menu", "Manage Company")}</span>
                        </Button>
                    )}
                    <Button variant={isArchived ? "outline" : "secondary"} className="h-10 justify-center" onClick={onToggleArchive} disabled={isUpdating || !ticket}>
                        {archiveIcon}<span className="text-sm">{archiveLabel}</span>
                    </Button>
                </div>
            </div>

            {/* DESKTOP */}
            <div className="hidden sm:flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                    <Button variant="ghost" onClick={onBack} size="sm" className="-ml-2">
                        <ArrowLeft className="w-4 h-4 mr-1" />{t("admin.support.backToTickets", "Back")}
                    </Button>
                    <div className="flex items-center gap-1 ml-2">
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasPrev} onClick={onPrev} title="Previous ticket (K)"><ChevronLeft className="w-4 h-4" /></Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasNext} onClick={onNext} title="Next ticket (J)"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={onRefresh} disabled={isRefreshing || !ticket} title={t("common.refresh", "Refresh")}>
                        <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                    </Button>
                    {showCompany && (
                        <Button variant="outline" size="sm" onClick={onManageCompany} title={t("admin.manageCompany.openTitle", "Manage company")}>
                            <Building className="w-4 h-4 mr-1.5" />{t("admin.manageCompany.menu", "Manage Company")}
                        </Button>
                    )}
                    <Button variant={inResolveUndo ? "outline" : "secondary"} size="sm" onClick={() => onQuickAction?.("resolve")} disabled={isUpdating || !ticket}
                        title={inResolveUndo ? t("admin.support.undoResolveTitle", "Revert resolve & archive (H)") : t("admin.support.resolveAndArchiveTitle", "Mark resolved and archive (H)")}>
                        {resolveIcon}{resolveLabel}
                        <kbd className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border border-border/60 text-muted-foreground">H</kbd>
                    </Button>
                    <Button variant={inCloseUndo ? "outline" : "secondary"} size="sm" onClick={() => onQuickAction?.("close")} disabled={isUpdating || !ticket}
                        title={inCloseUndo ? t("admin.support.undoCloseTitle", "Revert close & archive (C)") : t("admin.support.closeAndArchiveTitle", "Close and archive (C)")}>
                        {closeIcon}{closeLabel}
                        <kbd className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border border-border/60 text-muted-foreground">C</kbd>
                    </Button>
                    <Button variant={isArchived ? "outline" : "secondary"} size="sm" onClick={onToggleArchive} disabled={isUpdating || !ticket}>
                        {archiveIcon}{archiveLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}
